import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth'd, object = auth'd
  const [loading, setLoading] = useState(true);
  const [guilds, setGuilds] = useState([]);
  const [activeGuild, setActiveGuild] = useState(null);

  const loadGuilds = useCallback(async () => {
    try {
      const res = await api.get("/guilds/mine");
      setGuilds(res.data.guilds || []);
      const active = (res.data.guilds || []).find((g) => g.id === res.data.active_guild_id) || (res.data.guilds || [])[0] || null;
      setActiveGuild(active);
    } catch {
      setGuilds([]);
      setActiveGuild(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("auth_token");
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me", { headers: { Authorization: `Bearer ${token}` } });
      setUser(res.data);
      await loadGuilds();
    } catch {
      localStorage.removeItem("auth_token");
      setUser(false);
    }
    setLoading(false);
  }, [loadGuilds]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("auth_token", res.data.token);
    setUser(res.data.user);
    await loadGuilds();
    return res.data.user;
  };

  const register = async (email, password, name) => {
    const res = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("auth_token", res.data.token);
    setUser(res.data.user);
    await loadGuilds();
    return { user: res.data.user, needsGuild: res.data.needs_guild };
  };

  const logout = () => {
    localStorage.removeItem("auth_token");
    setUser(false);
    setGuilds([]);
    setActiveGuild(null);
  };

  const createGuild = async (name) => {
    const res = await api.post("/guilds", { name });
    localStorage.setItem("auth_token", res.data.token);
    await loadGuilds();
    return res.data.guild;
  };

  const switchGuild = async (guildId) => {
    const res = await api.post(`/guilds/${guildId}/switch`);
    localStorage.setItem("auth_token", res.data.token);
    // Reload user + guilds; full page reload is simplest for clean state
    await loadGuilds();
    window.location.reload();
  };

  const joinGuild = async (inviteCode) => {
    const res = await api.post("/guilds/join", { invite_code: inviteCode });
    localStorage.setItem("auth_token", res.data.token);
    await loadGuilds();
    return res.data.guild;
  };

  const createInvite = async (guildId, role = "officer") => {
    const res = await api.post(`/guilds/${guildId}/invite`, { role });
    return res.data;
  };

  const isAdmin = user?.role === "admin" || activeGuild?.role === "admin";
  const isOfficer = user?.role === "officer" || activeGuild?.role === "officer";
  const canWrite = isAdmin || isOfficer;
  const canDelete = isAdmin;

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout,
      isAdmin, isOfficer, canWrite, canDelete,
      guilds, activeGuild,
      createGuild, switchGuild, joinGuild, createInvite, refreshGuilds: loadGuilds,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
